import { useState } from "react";
import { ArrowUpRight, Check, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { submitInvestor, formatApiError } from "../lib/api";
import { Field } from "../components/ui-bits";

const initial = { name: "", email: "", fund_org: "", designation: "", message: "", company_website: "" };

export default function InvestorsPage() {
  return (
    <main className="relative min-h-screen overflow-hidden pt-[68px] pb-24" data-testid="investors-page">
      <div className="dot-grid absolute inset-0" />
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,77,255,0.16), transparent 70%)" }} />

      {/* headline */}
      <header className="relative py-16 lg:py-24" data-testid="investors-hero">
        <div className="container-x flex max-w-3xl flex-col gap-5">
          <span className="eyebrow">Investor Relations</span>
          <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-paper sm:text-5xl lg:text-6xl" data-testid="investors-headline">
            Building the Future of <span className="text-carza-lite text-glow">Car Care.</span>
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-mist sm:text-lg" data-testid="investors-sub">
            CARZA is building a technology-enabled car-care ecosystem connecting customers, service providers, and businesses through convenient booking and standardized service experiences.
          </p>
        </div>
      </header>

      <DeckRequest />
    </main>
  );
}

function DeckRequest() {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await submitInvestor(data);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="deck-form" className="relative py-8 lg:py-14" data-testid="investors-deck-section">
      <div className="container-x">
        <div className="mx-auto max-w-2xl">
          {done ? (
            <div className="glass flex flex-col items-center gap-4 rounded-[2rem] p-10 text-center" data-testid="investor-form-success">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-carza-dim text-carza"><Check size={26} /></span>
              <h2 className="font-display text-2xl font-bold text-paper">Thank you for your interest in CARZA. Your request has been received.</h2>
              <p className="text-sm leading-relaxed text-mist">Our team will reach out to {data.email} for manual follow-up.</p>
              <button onClick={() => { setDone(false); setData(initial); }} className="btn btn-outline mt-2 px-6 py-3 text-[13px]">Submit another request</button>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h2 className="font-display text-3xl font-bold tracking-tight text-paper sm:text-4xl" data-testid="investors-deck-title">Interested in CARZA?</h2>
                <p className="mx-auto mt-3 max-w-lg text-[15px] leading-relaxed text-mist" data-testid="investors-deck-sub">
                  Request our investor deck to learn more about our vision, market opportunity, traction, and growth plans.
                </p>
              </div>
              <form onSubmit={submit} className="glass mt-8 flex flex-col gap-4 rounded-[2rem] p-6 sm:p-8" data-testid="investor-form">
                <Field label="Full name">
                  <input className="field" value={data.name} onChange={set("name")} required placeholder="Your full name" autoComplete="name" data-testid="investor-name-input" />
                </Field>
                <Field label="Work email">
                  <input className="field" type="email" value={data.email} onChange={set("email")} required placeholder="you@fund.com" autoComplete="email" data-testid="investor-email-input" />
                </Field>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Organization / fund">
                    <input className="field" value={data.fund_org} onChange={set("fund_org")} required placeholder="Fund or organization" data-testid="investor-org-input" />
                  </Field>
                  <Field label="Designation">
                    <input className="field" value={data.designation} onChange={set("designation")} required placeholder="e.g. Principal" data-testid="investor-designation-input" />
                  </Field>
                </div>
                <Field label="Message" required={false}>
                  <textarea className="field min-h-[90px] resize-none" value={data.message} onChange={set("message")} placeholder="Optional — anything you'd like us to know…" data-testid="investor-message-input" />
                </Field>
                <input type="text" name="company_website" value={data.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="investor-error">{error}</p>}
                <button type="submit" disabled={loading} className="btn btn-primary w-full py-4 disabled:opacity-50" data-testid="investor-submit-btn">
                  {loading ? "Submitting…" : <>Submit Request <ArrowUpRight size={15} /></>}
                </button>
                <p className="flex items-center justify-center gap-4 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-mist-2">
                  <span className="flex items-center gap-1.5"><ShieldCheck size={11} className="text-carza" /> Stored securely</span>
                  <span className="flex items-center gap-1.5"><Mail size={11} className="text-carza" /> Reviewed manually</span>
                </p>
              </form>
              <p className="mt-5 text-center text-[11px] leading-relaxed text-mist-2" data-testid="investors-disclaimer">
                Requests are recorded for manual follow-up. We do not distribute financial models or decks automatically.
              </p>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
